import React, { useState, useEffect } from 'react';
import { Sentry } from '@/lib/sentry';
import { createRoot } from 'react-dom/client';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Mail, Send, Loader2, Eye } from 'lucide-react';
import { useSendDocument } from '@/hooks/useSendDocument';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import { processDynamicFields } from '@/components/cost-center/document-defaults/dynamicFieldProcessor';
import { format } from 'date-fns';
import { fetchInvoiceExportData } from '@/utils/invoiceExportUtils';
import InvoicePdfStylePreview from './preview/InvoicePdfStylePreview';
import InvoicePreviewModal from './preview/InvoicePreviewModal';
import { gridToHtml, jsonToGridData } from '@/components/settings/signature-builder/utils/signatureToHtml';
import RichTextEditorWithDynamicFields from '@/components/cost-center/document-defaults/RichTextEditorWithDynamicFields';
import { convertToEmailHtml, ensureHtml } from '@/utils/emailHtmlConverter';
import { formatCurrency } from '@/lib/utils';

interface SendInvoiceDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  invoiceId: string;
  invoiceNumber?: string;
  customerId?: string;
  companyId: string;
  siteAddress?: string;
}

const SendInvoiceDialog: React.FC<SendInvoiceDialogProps> = ({
  open,
  onOpenChange,
  invoiceId,
  invoiceNumber,
  customerId,
  companyId,
  siteAddress,
}) => {
  const [recipientEmail, setRecipientEmail] = useState('');
  const [recipientName, setRecipientName] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);

  const queryClient = useQueryClient();
  const { sendDocument, isSending } = useSendDocument();
  
  // Fetch invoice details for confirmation display
  const { data: invoiceDetails } = useQuery({
    queryKey: ['invoice-details-for-send', invoiceId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('invoices')
        .select('invoice_number, status, total_amount')
        .eq('id', invoiceId)
        .single();
      if (error) throw error;
      return data;
    },
    enabled: !!invoiceId && open,
  });

  // Fetch current user profile for dynamic fields
  const { data: userProfile } = useQuery({
    queryKey: ['user-profile-for-email'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return null;
      const { data, error } = await supabase
        .from('profiles')
        .select('first_name, last_name, email, personal_phone_number, email_signature, company_position')
        .eq('id', user.id)
        .single();
      if (error) throw error;
      return data;
    },
    enabled: open,
  });

  // Fetch company data for dynamic fields
  const { data: company } = useQuery({
    queryKey: ['company-for-email', companyId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('companies')
        .select('name, email, company_phone_number, street_address, suburb, city, province_state, postal_code, website, logo_url, "Bank Name", "Branch Name", "Branch Code", "Account Number", "Account Holder"')
        .eq('id', companyId)
        .single();
      if (error) throw error;
      return data;
    },
    enabled: !!companyId && open,
  });

  // Fetch customer data
  const { data: customer } = useQuery({
    queryKey: ['customer', customerId],
    queryFn: async () => {
      if (!customerId) return null;
      const { data, error } = await supabase
        .from('customers')
        .select('name, last_name, emails, business_email, customer_type, cell_numbers, street_address, suburb, city, province_state, postal_code')
        .eq('id', customerId)
        .single();
      if (error) throw error;
      return data;
    },
    enabled: !!customerId && open,
  });

  // Fetch invoice data for dynamic fields (including estimate_id)
  const { data: invoice } = useQuery({
    queryKey: ['invoice-for-email', invoiceId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('invoices')
        .select('invoice_number, invoice_date, due_date, estimate_id, site_street_address, site_suburb, site_city, site_province_state, site_postal_code')
        .eq('id', invoiceId)
        .single();
      if (error) throw error;
      return data;
    },
    enabled: !!invoiceId && open,
  });

  // Fetch linked estimate data for dynamic fields (including use_separate_site_address for fallback logic)
  const { data: linkedEstimate } = useQuery({
    queryKey: ['linked-estimate-for-invoice-email', invoice?.estimate_id],
    queryFn: async () => {
      if (!invoice?.estimate_id) return null;
      const { data, error } = await supabase
        .from('estimates')
        .select('estimate_number, created_at, expires_at, validity_period, use_separate_site_address, site_street_address, site_suburb, site_city, site_province_state, site_postal_code')
        .eq('id', invoice.estimate_id)
        .single();
      if (error) {
        console.error('[SendInvoiceDialog] Error fetching linked estimate:', error);
        return null;
      }
      return data;
    },
    enabled: !!invoice?.estimate_id && open,
  });

  // Fetch document defaults for email template and signature template
  const { data: documentDefaults } = useQuery({
    queryKey: ['document-defaults', companyId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('document_defaults')
        .select('invoice_email_template, invoice_email_subject, invoice_footer, email_signature_template')
        .eq('company_id', companyId)
        .single();
      if (error && error.code !== 'PGRST116') throw error;
      return data;
    },
    enabled: !!companyId && open,
  });

  // Build dynamic field data object - reusable across subject, message, and signature
  const buildDynamicFieldData = () => {
    const customerFullName = customer ? `${customer.name} ${customer.last_name || ''}`.trim() : '';
    const customerEmail = customer?.customer_type === 'commercial' 
      ? customer.business_email 
      : customer?.emails?.[0];
    const today = format(new Date(), 'dd MMMM yyyy');
    
    // Determine site address with fallback logic:
    // 1. If estimate has use_separate_site_address enabled AND has site data, use estimate's site address
    // 2. Otherwise fall back to customer address
    const useEstimateSiteAddress = linkedEstimate?.use_separate_site_address && linkedEstimate?.site_street_address;
    const siteAddressFields = useEstimateSiteAddress ? {
      street_address: linkedEstimate.site_street_address,
      suburb: linkedEstimate.site_suburb,
      city: linkedEstimate.site_city,
      province_state: linkedEstimate.site_province_state,
      postal_code: linkedEstimate.site_postal_code,
    } : {
      street_address: customer?.street_address,
      suburb: customer?.suburb,
      city: customer?.city,
      province_state: customer?.province_state,
      postal_code: customer?.postal_code,
    };

    return {
      user: {
        first_name: userProfile?.first_name || undefined,
        last_name: userProfile?.last_name || undefined,
        email: userProfile?.email || undefined,
        personal_phone_number: userProfile?.personal_phone_number || undefined,
        company_position: userProfile?.company_position || undefined,
      },
      company: {
        name: company?.name || undefined,
        email: company?.email || undefined,
        company_phone_number: company?.company_phone_number || undefined,
        street_address: company?.street_address || undefined,
        suburb: company?.suburb || undefined,
        city: company?.city || undefined,
        province_state: company?.province_state || undefined,
        postal_code: company?.postal_code || undefined,
        website: company?.website || undefined,
        bank_name: company?.['Bank Name'] || undefined,
        branch_name: company?.['Branch Name'] || undefined,
        branch_code: company?.['Branch Code'] || undefined,
        account_number: company?.['Account Number'] || undefined,
        account_holder: company?.['Account Holder'] || undefined,
      },
      customer: {
        name: customerFullName || undefined,
        first_name: customer?.name || undefined,
        last_name: customer?.last_name || undefined,
        email: customerEmail || undefined,
        cell_numbers: customer?.cell_numbers || undefined,
        street_address: customer?.street_address || undefined,
        suburb: customer?.suburb || undefined,
        city: customer?.city || undefined,
        province_state: customer?.province_state || undefined,
        postal_code: customer?.postal_code || undefined,
      },
      invoice: {
        invoice_number: invoice?.invoice_number || invoiceNumber || undefined,
        invoice_date: invoice?.invoice_date ? format(new Date(invoice.invoice_date), 'dd MMMM yyyy') : undefined,
        due_date: invoice?.due_date ? format(new Date(invoice.due_date), 'dd MMMM yyyy') : undefined,
      },
      estimate: {
        estimate_number: linkedEstimate?.estimate_number || 'N/A',
        created_date: linkedEstimate?.created_at 
          ? format(new Date(linkedEstimate.created_at), 'dd MMMM yyyy') 
          : 'N/A',
        expires_date: linkedEstimate?.expires_at 
          ? format(new Date(linkedEstimate.expires_at), 'dd MMMM yyyy') 
          : 'N/A',
        validity_days: linkedEstimate?.validity_period,
        site_street_address: siteAddressFields.street_address || undefined,
        site_suburb: siteAddressFields.suburb || undefined,
        site_city: siteAddressFields.city || undefined,
        site_province_state: siteAddressFields.province_state || undefined,
        site_postal_code: siteAddressFields.postal_code || undefined,
      },
      project: {
        site_street_address: siteAddressFields.street_address || undefined,
        site_suburb: siteAddressFields.suburb || undefined,
        site_city: siteAddressFields.city || undefined,
        site_province_state: siteAddressFields.province_state || undefined,
        site_postal_code: siteAddressFields.postal_code || undefined,
      },
      system: {
        today_date: today,
      },
    };
  };

  // Pre-fill form when data loads
  useEffect(() => {
    if (customer) {
      const fullName = `${customer.name} ${customer.last_name || ''}`.trim();
      setRecipientName(fullName);
      
      // Get primary email
      const email = customer.customer_type === 'commercial' 
        ? customer.business_email 
        : customer.emails?.[0];
      if (email) setRecipientEmail(email);
    }
  }, [customer]);

  useEffect(() => {
    if (documentDefaults?.invoice_email_subject && customer) {
      const dynamicData = buildDynamicFieldData();
      const processedSubject = processDynamicFields(documentDefaults.invoice_email_subject, dynamicData);
      setSubject(processedSubject);
    } else if (invoiceNumber) {
      setSubject(`Invoice ${invoiceNumber}`);
    }
  }, [documentDefaults, invoiceNumber, customer, userProfile, company, invoice, linkedEstimate, siteAddress]);

  useEffect(() => {
    if (documentDefaults?.invoice_email_template && customer) {
      const dynamicData = buildDynamicFieldData();
      const processedTemplate = processDynamicFields(documentDefaults.invoice_email_template, dynamicData);
      setMessage(processedTemplate);
    } else if (customer) {
      // Default message
      setMessage(`Hi ${customer.name},\n\nPlease find your invoice attached.\n\nBest regards`);
    }
  }, [documentDefaults, customer, userProfile, company, invoice, linkedEstimate, siteAddress, invoiceNumber]);

  // Helper function to render InvoicePdfStylePreview and capture HTML
  const renderInvoicePreviewToHtml = async (exportData: any): Promise<string> => {
    const container = document.createElement('div');
    container.style.position = 'absolute';
    container.style.left = '-9999px';
    container.style.top = '0';
    document.body.appendChild(container);

    try {
      const root = createRoot(container);
      await new Promise<void>((resolve) => {
        root.render(
          <InvoicePdfStylePreview
            invoice={exportData.invoice}
            lineItems={exportData.lineItems}
            customer={exportData.customer}
            company={exportData.company}
            userProfile={exportData.userProfile}
            documentDefaults={exportData.documentDefaults}
          />
        );
        setTimeout(resolve, 150);
      });

      const previewElement = container.querySelector('#invoice-preview');
      if (!previewElement) {
        throw new Error('Preview element not found');
      }

      return previewElement.outerHTML;
    } finally {
      document.body.removeChild(container);
    }
  };

  // Helper to update invoice status from draft to sent
  const updateInvoiceStatusToSent = async (invId: string): Promise<boolean> => {
    // Get the current invoice status
    const { data: inv, error: fetchError } = await supabase
      .from('invoices')
      .select('status')
      .eq('id', invId)
      .single();
    
    if (fetchError) {
      console.error('[SendInvoiceDialog] Error fetching invoice status:', fetchError);
      throw fetchError;
    }
    
    if (inv?.status !== 'draft') {
      return false; // Not a draft, no change needed
    }
    
    // Update status to 'sent'
    const { error: updateError } = await supabase
      .from('invoices')
      .update({ status: 'sent' })
      .eq('id', invId);
    
    if (updateError) {
      console.error('[SendInvoiceDialog] Error updating invoice status:', updateError);
      throw updateError;
    }
    
    console.log('[SendInvoiceDialog] Updated invoice status from draft to sent');
    return true; // Status was updated
  };

  // Helper to revert invoice status back to draft on failure
  const revertInvoiceStatusToDraft = async (invId: string): Promise<void> => {
    const { error } = await supabase
      .from('invoices')
      .update({ status: 'draft' })
      .eq('id', invId);
    
    if (error) {
      console.error('[SendInvoiceDialog] Error reverting invoice status:', error);
    } else {
      console.log('[SendInvoiceDialog] Reverted invoice status to draft');
    }
  };

  const handleSend = async () => {
    if (!recipientEmail) {
      toast.error('Please enter a recipient email');
      return;
    }

    if (!recipientEmail.includes('@')) {
      toast.error('Please enter a valid email address');
      return;
    }

    setIsGeneratingPdf(true);
    let statusWasUpdated = false;

    try {
      // Step 1: Update status from 'draft' to 'sent' BEFORE generating PDF
      statusWasUpdated = await updateInvoiceStatusToSent(invoiceId);

      // Step 2: Fetch complete invoice data using the existing utility (now reflects 'sent' status)
      console.log('[SendInvoiceDialog] Fetching invoice data for PDF...');
      const exportData = await fetchInvoiceExportData(invoiceId);

      // Render the preview component and capture its HTML
      console.log('[SendInvoiceDialog] Rendering preview component...');
      const previewHtml = await renderInvoicePreviewToHtml(exportData);

      // Call edge function to enhance HTML with proper styles
      console.log('[SendInvoiceDialog] Enhancing HTML via edge function...');
      const { data: enhancedResponse, error: enhanceError } = await supabase.functions.invoke('generate-invoice-pdf', {
        body: {
          htmlContent: previewHtml,
          invoiceData: {
            invoice: exportData.invoice,
            customer: exportData.customer,
            company: exportData.company,
            userProfile: exportData.userProfile,
            documentDefaults: exportData.documentDefaults,
            estimate: exportData.invoice.estimates,
          }
        }
      });

      if (enhanceError) {
        console.error('[SendInvoiceDialog] Error enhancing HTML:', enhanceError);
        throw new Error(enhanceError.message);
      }

      // Generate PDF using Puppeteer/Browserless (same as estimates)
      console.log('[SendInvoiceDialog] Generating PDF via Browserless...');
      const { data: pdfResponse, error: pdfError } = await supabase.functions.invoke('generate-pdf-puppeteer', {
        body: {
          htmlContent: enhancedResponse.htmlContent
        }
      });

      if (pdfError) {
        console.error('[SendInvoiceDialog] Error generating PDF:', pdfError);
        throw new Error(pdfError.message);
      }

      if (!pdfResponse?.pdfBase64) {
        throw new Error('No PDF data returned from Browserless');
      }

      const pdfBase64 = pdfResponse.pdfBase64;
      const pdfFilename = `Invoice_${invoiceNumber || invoiceId}.pdf`;

      console.log('[SendInvoiceDialog] PDF generated, size:', pdfBase64.length);

      // Process email signature - prefer company template, fall back to user's personal signature
      const dynamicData = buildDynamicFieldData();
      let processedSignature = '';
      if (documentDefaults?.email_signature_template) {
        const gridData = jsonToGridData(documentDefaults.email_signature_template);
        if (gridData) {
          // Use absolute URLs for email so icons work in all email clients
          const baseUrl = typeof window !== 'undefined' ? window.location.origin : '';
          processedSignature = gridToHtml(gridData, {
            user: {
              first_name: userProfile?.first_name,
              last_name: userProfile?.last_name,
              email: userProfile?.email,
              personal_phone_number: userProfile?.personal_phone_number,
              company_position: userProfile?.company_position,
            },
            company: {
              name: company?.name,
              email: company?.email,
              company_phone_number: company?.company_phone_number,
              website: company?.website,
            },
          }, { baseUrl });
        }
      } else if (userProfile?.email_signature) {
        processedSignature = processDynamicFields(userProfile.email_signature, dynamicData);
      }

      // Convert message to email-safe HTML
      const messageHtml = ensureHtml(message);
      const emailSafeMessage = convertToEmailHtml(messageHtml, { baseUrl: typeof window !== 'undefined' ? window.location.origin : '' });
      
      // Build final email body with signature
      const bodyHtml = `
        <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0;">
          ${emailSafeMessage}
          ${processedSignature ? `
            <div style="margin-top: 20px; padding-top: 15px; border-top: 1px solid #e5e5e5;">
              ${processedSignature}
            </div>
          ` : ''}
        </div>
      `;

      await sendDocument({
        documentType: 'invoice',
        documentId: invoiceId,
        recipientEmail,
        recipientName,
        subject,
        bodyHtml,
        pdfBase64,
        pdfFilename,
        companyId,
      });

      // Invalidate queries to refresh UI with new status
      queryClient.invalidateQueries({ queryKey: ['invoice', invoiceId] });
      queryClient.invalidateQueries({ queryKey: ['invoices'] });

      onOpenChange(false);
    } catch (error: any) {
      // Rollback status if we updated it
      if (statusWasUpdated) {
        await revertInvoiceStatusToDraft(invoiceId);
      }

      console.error('[SendInvoiceDialog] Error sending invoice:', error);
      toast.error(`Failed to send invoice: ${error.message}`);
    } finally {
      setIsGeneratingPdf(false);
    }
  };

  const isLoading = isSending || isGeneratingPdf;
  const isDataLoading = !userProfile || !company || !customer || !documentDefaults;

  return (
    <Sentry.ErrorBoundary fallback={<div className="p-8 text-center text-destructive">Something went wrong loading the send dialog.</div>}>
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5" />
            Send Invoice
          </DialogTitle>
          <DialogDescription>
            Send this invoice to your customer via email with PDF attachment.
          </DialogDescription>
        </DialogHeader>

        {isDataLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            <span className="ml-2 text-muted-foreground">Loading template...</span>
          </div>
        ) : (
          <div className="space-y-4 py-4">
            {/* Invoice Confirmation Section */}
            {invoiceDetails && (
              <div className="rounded-lg border bg-muted/50 p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">
                      You're about to send:
                    </p>
                    <p className="text-lg font-semibold">
                      Invoice #{invoiceDetails.invoice_number}
                    </p>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-sm text-muted-foreground">
                        Total: {formatCurrency(invoiceDetails.total_amount || 0)}
                      </span>
                      {invoiceDetails.status === 'draft' && (
                        <Badge variant="outline" className="text-xs">Draft</Badge>
                      )}
                    </div>
                  </div>
                  <InvoicePreviewModal
                    invoiceId={invoiceId}
                    triggerButton={
                      <Button 
                        variant="outline" 
                        size="sm"
                      >
                        <Eye className="w-4 h-4 mr-1" />
                        Preview
                      </Button>
                    }
                  />
                </div>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="recipient-email">Recipient Email *</Label>
              <Input
                id="recipient-email"
                type="email"
                placeholder="customer@example.com"
                value={recipientEmail}
                onChange={(e) => setRecipientEmail(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="recipient-name">Recipient Name</Label>
              <Input
                id="recipient-name"
                placeholder="John Doe"
                value={recipientName}
                onChange={(e) => setRecipientName(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="subject">Subject</Label>
              <Input
                id="subject"
                placeholder="Your invoice"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label>Message</Label>
              <RichTextEditorWithDynamicFields
                value={message}
                onChange={setMessage}
                placeholder="Enter your message..."
                minHeight="180px"
              />
            </div>
          </div>
        )}


        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isLoading}>
            Cancel
          </Button>
          <Button onClick={handleSend} disabled={isLoading}>
            {isLoading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                {isGeneratingPdf ? 'Generating PDF...' : 'Sending...'}
              </>
            ) : (
              <>
                <Send className="h-4 w-4 mr-2" />
                Send Email
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    </Sentry.ErrorBoundary>
  );
};

export default SendInvoiceDialog;
